import { useEffect, useState } from 'react';
import { Alert, App, Form, Input, Modal, Space, Typography } from 'antd';
import {
  ADMIN_PASSWORD_MAX_LENGTH,
  ADMIN_PASSWORD_MIN_LENGTH,
  changeOwnPassword,
} from '../../api/admin-accounts';
import { errorText } from '../../components/ErrorAlert';

interface OwnPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

interface OwnPasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const PASSWORD_RULES = [
  { required: true, message: '请填写密码' },
  {
    min: ADMIN_PASSWORD_MIN_LENGTH,
    max: ADMIN_PASSWORD_MAX_LENGTH,
    message: `密码长度需为 ${ADMIN_PASSWORD_MIN_LENGTH}–${ADMIN_PASSWORD_MAX_LENGTH} 位`,
  },
];

/**
 * OPT-06 本人改密（23 号 §5.3）：登录即可使用，校验当前密码，
 * 成功后服务端撤销本人其他会话（保留当前会话）。密码不落日志、不写审计明文。
 */
export function OwnPasswordModal({ open, onClose }: OwnPasswordModalProps): React.ReactElement {
  const { message } = App.useApp();
  const [form] = Form.useForm<OwnPasswordFormValues>();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      form.resetFields();
    }
  }, [open, form]);

  const handleSubmit = async (): Promise<void> => {
    let values: OwnPasswordFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      const result = await changeOwnPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success(`密码已修改，已撤销本人其他 ${result.revokedOtherSessions} 个会话`);
      form.resetFields();
      onClose();
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="修改我的密码"
      okText="确认修改"
      cancelText="取消"
      confirmLoading={submitting}
      onCancel={onClose}
      onOk={() => void handleSubmit()}
      destroyOnHidden
    >
      <Space orientation="vertical" size={12} style={{ width: '100%' }}>
        <Alert
          type="info"
          showIcon
          title="修改后本人其他登录会话将失效"
          description="当前会话保持有效；如怀疑凭据泄露，请同时联系超级管理员重置。"
        />
        <Form<OwnPasswordFormValues> form={form} layout="vertical">
          <Form.Item name="currentPassword" label="当前密码" rules={PASSWORD_RULES}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item name="newPassword" label="新密码" rules={PASSWORD_RULES}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator: (_rule, value) =>
                  !value || getFieldValue('newPassword') === value
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次输入的新密码不一致')),
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
        <Typography.Text type="secondary">
          密码仅用于提交，不会显示在页面或写入日志、审计与报告。
        </Typography.Text>
      </Space>
    </Modal>
  );
}

export default OwnPasswordModal;
